import { useState, useCallback, useEffect } from 'react';
import { classifyInvitedUserAdvertisersFromApi } from '../utils/classifyAdvertiserAccess';

export const useAdvertiserData = (email, onCurrentAccessDataChange, allUsersList = []) => {
  const [editableInvitedUserData, setEditableInvitedUserData] = useState({
    advertisers_utc: [],
    advertisers_non_utc: [],
    advertisers_ctv: [],
    advertisers_gtv: [],
    org_utc: []
  });

  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user advertiser details from API
  const fetchUserAdvertiserDetails = useCallback(
    async (email) => {
      if (!email || !email.includes('@inmobi.com')) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch('/get_user_advertiser_details', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        // Handle different response scenarios
        if (response.status === 404) {
          // User not found - this is a new user
          setError(`User ${email} not found. This appears to be a new user. You can proceed to add organizations and advertisers.`);

          // Initialize empty data for new user
          setEditableInvitedUserData({
            email: email,
            advertisers_utc: [],
            advertisers_non_utc: [],
            advertisers_ctv: [],
            advertisers_gtv: [],
            org_utc: []
          });
          setHasFetchedData(true);
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${data?.error || 'Unknown error'}`);
        }

        if (data?.data && Array.isArray(data.data)) {
          console.log('🔍 useAdvertiserData: Raw API response data length:', data.data.length);
          console.log('🔍 useAdvertiserData: Raw API response data:', data.data);

          // Check for duplicates in raw data
          const advertiserNames = data.data.map((item) => item.advertiser_name);
          const uniqueNames = [...new Set(advertiserNames)];

          // Show duplicate advertisers in detail
          const duplicateNames = advertiserNames.filter((name, index) => advertiserNames.indexOf(name) !== index);
          if (duplicateNames.length > 0) {
            console.log('🔍 useAdvertiserData: Duplicate advertisers found:', duplicateNames);
            const duplicateItems = data.data.filter((item) => duplicateNames.includes(item.advertiser_name));
            console.table(
              duplicateItems.map((item) => ({
                advertiser_name: item.advertiser_name,
                advertiser_guid: item.advertiser_guid,
                timezone: item.timezone,
                selected: item.selected,
                org_name: item.org_name
              }))
            );
          }

          // Smart filtering with fallbacks
          let selectedData = data.data.filter((item) => item.selected === 'true');

          if (selectedData.length === 0) {
            const allSelectedFalse = data.data.every((item) => item.selected === 'false');
            if (allSelectedFalse) {
              console.log('🔍 All advertisers have selected=false, treating as selected');
              selectedData = data.data;
            }
          }

          if (selectedData.length === 0) {
            console.log('🔍 No selected advertisers found, using all as fallback');
            selectedData = data.data;
          }

          // Deduplicate based on advertiser_guid (keep first occurrence)
          const uniqueSelectedData = [];
          const seenGuids = new Set();

          for (const item of selectedData) {
            if (!seenGuids.has(item.advertiser_guid)) {
              seenGuids.add(item.advertiser_guid);
              uniqueSelectedData.push(item);
            }
          }

          // Use deduplicated data for further processing
          selectedData = uniqueSelectedData;

          const classified = classifyInvitedUserAdvertisersFromApi(selectedData);

          // Log empty timezone advertisers for investigation (DSP buckets only use explicit UTC/JST)
          const emptyTimezoneAdvertisers = selectedData.filter((item) => item.timezone === '');
          if (emptyTimezoneAdvertisers.length > 0) {
            console.log('🔍 useAdvertiserData: Found advertisers with empty timezone:', {
              count: emptyTimezoneAdvertisers.length,
              advertisers: emptyTimezoneAdvertisers.map((item) => ({
                name: item.advertiser_name,
                org: item.org_name,
                timezone: item.timezone
              }))
            });
          }

          // Get organizations and role from allUsersList
          let orgUtc = [];
          let userRole = null;
          if (allUsersList && allUsersList.length > 0) {
            const invitedUser = allUsersList.find((user) => user.email_id === email);
            if (invitedUser) {
              // Get organizations
              if (invitedUser.org_utc) {
                if (Array.isArray(invitedUser.org_utc)) {
                  orgUtc = invitedUser.org_utc;
                } else if (typeof invitedUser.org_utc === 'string') {
                  orgUtc = invitedUser.org_utc
                    .split(',')
                    .map((org) => org.trim())
                    .filter(Boolean);
                }
              }
              // Get user role
              userRole = invitedUser.role;
            }
          }

          const processedData = {
            advertisers_utc: classified.advertisers_utc,
            advertisers_non_utc: classified.advertisers_non_utc,
            advertisers_ctv: classified.advertisers_ctv,
            advertisers_gtv: classified.advertisers_gtv,
            org_utc: orgUtc, // Populated from allUsersList
            role: userRole // Include user role
          };

          // Check for duplicates in final data
          const allAdvertiserNames = [
            ...processedData.advertisers_utc.map((item) => item.name),
            ...processedData.advertisers_non_utc.map((item) => item.name),
            ...processedData.advertisers_ctv.map((item) => item.name),
            ...processedData.advertisers_gtv.map((item) => item.name)
          ];
          const uniqueAdvertiserNames = [...new Set(allAdvertiserNames)];

          // Show duplicate advertisers in final data in detail
          const finalDuplicateNames = allAdvertiserNames.filter((name, index) => allAdvertiserNames.indexOf(name) !== index);
          if (finalDuplicateNames.length > 0) {
            console.log('🔍 useAdvertiserData: Final duplicates found:', finalDuplicateNames);
            const finalDuplicateItems = [
              ...processedData.advertisers_utc,
              ...processedData.advertisers_non_utc,
              ...processedData.advertisers_ctv,
              ...processedData.advertisers_gtv
            ].filter((item) => finalDuplicateNames.includes(item.name));
            console.table(
              finalDuplicateItems.map((item) => ({
                name: item.name,
                id: item.id,
                timezone: item.timezone,
                org_name: item.org_name
              }))
            );
          }

          setEditableInvitedUserData(processedData);
          setHasFetchedData(true);

          // Notify parent
          if (onCurrentAccessDataChange) {
            onCurrentAccessDataChange(processedData);
          }
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        console.error('🔍 Error fetching advertiser details:', err);
        setError(err.message);

        // Set empty data on error
        const emptyData = {
          advertisers_utc: [],
          advertisers_non_utc: [],
          advertisers_ctv: [],
          advertisers_gtv: [],
          org_utc: []
        };
        setEditableInvitedUserData(emptyData);
        setHasFetchedData(true);

        if (onCurrentAccessDataChange) {
          onCurrentAccessDataChange(emptyData);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onCurrentAccessDataChange]
  );

  // Reset data when email changes
  useEffect(() => {
    if (email && email.includes('@inmobi.com')) {
      setHasFetchedData(false);
      setEditableInvitedUserData({
        advertisers_utc: [],
        advertisers_non_utc: [],
        advertisers_ctv: [],
        advertisers_gtv: [],
        org_utc: []
      });
      setError(null);
    }
  }, [email]);

  // Update organizations from allUsersList
  const updateOrganizations = useCallback((organizations) => {
    setEditableInvitedUserData((prev) => ({
      ...prev,
      org_utc: organizations || []
    }));
  }, []);

  // Add advertiser
  const addAdvertiser = useCallback(
    (advertiser, timezone, platform = 'DSP') => {
      setEditableInvitedUserData((prev) => {
        const newData = { ...prev };

        if (platform === 'CTV') {
          const row = { ...advertiser, timezone: 'UTC' };
          newData.advertisers_ctv = [...(newData.advertisers_ctv || []), row];
        } else if (platform === 'GTV') {
          const row = { ...advertiser, timezone: 'UTC' };
          newData.advertisers_gtv = [...(newData.advertisers_gtv || []), row];
        } else if (timezone === 'UTC') {
          newData.advertisers_utc = [...(newData.advertisers_utc || []), advertiser];
        } else if (timezone === 'JST') {
          newData.advertisers_non_utc = [...(newData.advertisers_non_utc || []), advertiser];
        }

        // Notify parent
        if (onCurrentAccessDataChange) {
          onCurrentAccessDataChange(newData);
        }

        return newData;
      });
    },
    [onCurrentAccessDataChange]
  );

  // Remove advertiser
  const removeAdvertiser = useCallback(
    (advertiserName, timezone, platform = 'DSP') => {
      setEditableInvitedUserData((prev) => {
        const newData = { ...prev };

        if (platform === 'CTV') {
          newData.advertisers_ctv = (newData.advertisers_ctv || []).filter((adv) => adv.name !== advertiserName);
        } else if (platform === 'GTV') {
          newData.advertisers_gtv = (newData.advertisers_gtv || []).filter((adv) => adv.name !== advertiserName);
        } else if (timezone === 'UTC') {
          newData.advertisers_utc = newData.advertisers_utc.filter((adv) => adv.name !== advertiserName);
        } else if (timezone === 'JST') {
          newData.advertisers_non_utc = newData.advertisers_non_utc.filter((adv) => adv.name !== advertiserName);
        }

        // Notify parent
        if (onCurrentAccessDataChange) {
          onCurrentAccessDataChange(newData);
        }

        return newData;
      });
    },
    [onCurrentAccessDataChange]
  );

  return {
    editableInvitedUserData,
    hasFetchedData,
    isLoading,
    error,
    fetchUserAdvertiserDetails,
    updateOrganizations,
    addAdvertiser,
    removeAdvertiser
  };
};
